import React, {useRef, useState} from "react";
import {Modal, Form, InputGroup, Row, Col, Card} from "react-bootstrap";
import {Button} from "laravel-inertia-backend";
import {router} from "@inertiajs/react";
import axios from "axios";

/**
 * Excel-Upload für Kampagnen-Empfänger (Sheet "Mail-Liste"): Upload ->
 * Vorschau (Zähler + Beispielzeilen) -> Bestätigen -> Import mit
 * Ergebnis-Zählern. Analog ImportPositionsModal (Weclapp), aber ohne
 * CSV-Mapping-Optionen, da das Excel-Format mit 13 festen Spalten
 * verbindlich vorgegeben ist.
 */
export default function RecipientImportModal({show, campaignId, onClose}) {
    const [file, setFile] = useState(null);
    const [error, setError] = useState(null);
    const [preview, setPreview] = useState(null);
    const [result, setResult] = useState(null);
    const [step, setStep] = useState(1); // 1 Upload, 2 Vorschau, 3 Import läuft, 4 Fertig
    const [loading, setLoading] = useState(false);

    const dropRef = useRef(null);

    const resetAll = () => {
        setFile(null);
        setError(null);
        setPreview(null);
        setResult(null);
        setStep(1);
        setLoading(false);
    };

    const handleFileChange = (f) => {
        setFile(f || null);
        setError(null);
        setPreview(null);
        setStep(1);
    };

    const runAnalyze = async () => {
        if (!file) {
            setError("Bitte eine Excel-Datei (.xlsx) auswählen.");
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const form = new FormData();
            form.append("file", file);
            const {data} = await axios.post(`/campaigns/${campaignId}/recipients/import/analyze`, form);
            setPreview(data);
            setStep(2);
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Analyse fehlgeschlagen");
        } finally {
            setLoading(false);
        }
    };

    const commitImport = async () => {
        setStep(3);
        setLoading(true);
        setError(null);
        try {
            const {data} = await axios.post(`/campaigns/${campaignId}/recipients/import`);
            setResult(data);
            setStep(4);
            router.reload({only: ["recipients"]});
        } catch (e) {
            setError(e.response?.data?.message || e.message || "Import fehlgeschlagen");
            setStep(2);
        } finally {
            setLoading(false);
        }
    };

    const closeModal = () => {
        onClose && onClose();
        resetAll();
    };

    return (
        <Modal show={show} onHide={closeModal} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Empfänger aus Excel importieren</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && <div className="alert alert-danger">{error}</div>}

                <div className="mb-3">
                    <ol className="breadcrumb">
                        <li className={`breadcrumb-item ${step === 1 ? "active" : ""}`}>1. Upload</li>
                        <li className={`breadcrumb-item ${step === 2 ? "active" : ""}`}>2. Vorschau</li>
                        <li className={`breadcrumb-item ${step === 3 ? "active" : ""}`}>3. Import</li>
                        <li className={`breadcrumb-item ${step === 4 ? "active" : ""}`}>4. Ergebnis</li>
                    </ol>
                </div>

                {step === 1 && (
                    <div>
                        <div className="mb-2 text-muted">
                            Erwartet wird das Sheet "Mail-Liste" mit den 13 festen Spalten
                            (Ferienende, Versanddatum, Schulname, ..., Rückmeldung). Das
                            Sheet "Ohne Mail-Adresse" wird ignoriert.
                        </div>
                        <InputGroup>
                            <Form.Control type="text" readOnly placeholder="Keine Datei ausgewählt"
                                          value={file ? file.name : ""}/>
                            <Button variant="secondary" onClick={() => dropRef.current?.click()}>
                                Datei auswählen
                            </Button>
                        </InputGroup>
                        <input ref={dropRef} type="file" accept=".xlsx,.xls" className="d-none"
                               onChange={(e) => handleFileChange(e.target.files?.[0] || null)}/>
                    </div>
                )}

                {step === 2 && preview && (
                    <div>
                        <Card className="mb-3">
                            <Card.Body>
                                <div><strong>Zusammenfassung</strong></div>
                                <div>Gelesene Zeilen: {preview.counters?.gesamt_zeilen ?? 0}</div>
                                <div>Werden importiert: {preview.counters?.importiert ?? 0}</div>
                                <div>Ohne E-Mail übersprungen: {preview.counters?.ohne_email_uebersprungen ?? 0}</div>
                                <div>Ohne Versanddatum übersprungen: {preview.counters?.ohne_datum_uebersprungen ?? 0}</div>
                                <div>Duplikate übersprungen: {preview.counters?.duplikate_uebersprungen ?? 0}</div>
                            </Card.Body>
                        </Card>
                        <div className="mb-2"><strong>Vorschau (erste {preview.sample?.length ?? 0} Zeilen)</strong></div>
                        <div className="table-responsive">
                            <table className="table table-sm table-bordered">
                                <thead>
                                <tr>
                                    <th>Schule</th>
                                    <th>Stadt</th>
                                    <th>E-Mail</th>
                                    <th>Schulform</th>
                                    <th>Frühester Versand</th>
                                </tr>
                                </thead>
                                <tbody>
                                {(preview.sample || []).map((row, i) => (
                                    <tr key={i}>
                                        <td>{row.school_name}</td>
                                        <td>{row.city}</td>
                                        <td>{row.email}</td>
                                        <td>{row.school_type}</td>
                                        <td>{row.earliest_send_at}</td>
                                    </tr>
                                ))}
                                {(!preview.sample || preview.sample.length === 0) && (
                                    <tr>
                                        <td colSpan={5} className="text-muted">Keine gültigen Zeilen gefunden.</td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {step === 3 && (
                    <div className="text-center py-4">Empfänger werden importiert…</div>
                )}

                {step === 4 && result && (
                    <div className="text-center py-4">
                        <div className="text-success fw-bold">Import abgeschlossen</div>
                        <Row className="mt-3 justify-content-center">
                            <Col xs={8}>
                                <div>Importiert: {result.counters?.importiert ?? 0}</div>
                                <div>Ohne E-Mail übersprungen: {result.counters?.ohne_email_uebersprungen ?? 0}</div>
                                <div>Ohne Versanddatum übersprungen: {result.counters?.ohne_datum_uebersprungen ?? 0}</div>
                                <div>Duplikate übersprungen: {result.counters?.duplikate_uebersprungen ?? 0}</div>
                            </Col>
                        </Row>
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={closeModal}>
                    {step === 4 ? "Schließen" : "Abbrechen"}
                </Button>
                {step === 1 && (
                    <Button variant="primary" onClick={runAnalyze} loading={loading} disabled={!file}>
                        Analysieren
                    </Button>
                )}
                {step === 2 && (
                    <>
                        <Button variant="outline-secondary" onClick={() => setStep(1)}>Zurück</Button>
                        <Button variant="primary" onClick={commitImport} disabled={loading}>
                            Übernehmen
                        </Button>
                    </>
                )}
            </Modal.Footer>
        </Modal>
    );
}
